import React, { useEffect } from 'react'
import styled from 'styled-components'
import { useNavigate } from 'react-router-dom'

const Container = styled.div`
  width: 100%;
  min-height: 100vh;
  background-color: #ffffff;
  font-family: -apple-system, system-ui, BlinkMacSystemFont, 'Segoe UI', Helvetica, Arial, sans-serif;
  font-size: 18px;
  line-height: 1.8;
  color: #2d3748;
`

const HeroSection = styled.section`
  padding-top: 100px;
  padding-bottom: 60px;
  background: linear-gradient(135deg, #7c3aed 0%, #4f46e5 100%);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, rgba(124, 58, 237, 0.9) 0%, rgba(79, 70, 229, 0.9) 100%);
    z-index: 0;
  }

  @media (max-width: 800px) {
    padding-top: 80px;
    padding-bottom: 40px;
  }
`

const MaxWidthContainer = styled.div`
  max-width: 1300px;
  margin: 0 auto;
  padding: 0 30px;
  position: relative;
  z-index: 1;

  @media (max-width: 800px) {
    padding: 0 20px;
  }
`

const HeroContent = styled.div`
  text-align: center;
  max-width: 900px;
  margin: 0 auto;
`

const ProductLogo = styled.h1`
  font-size: 56px;
  font-weight: 700;
  color: #ffffff;
  margin-bottom: 20px;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

  @media (max-width: 800px) {
    font-size: 40px;
  }
`

const HeroTagline = styled.p`
  font-size: 24px;
  font-weight: 300;
  color: rgba(255, 255, 255, 0.95);
  margin-bottom: 30px;

  @media (max-width: 800px) {
    font-size: 20px;
  }
`

const HeroDescription = styled.p`
  font-size: 18px;
  line-height: 1.8;
  color: rgba(255, 255, 255, 0.9);
  margin-bottom: 40px;
`

const CTAButton = styled.button`
  background: #ffffff;
  color: #ec4899;
  font-size: 18px;
  font-weight: 700;
  padding: 18px 50px;
  border: none;
  border-radius: 9999px;
  cursor: pointer;
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.3);
  transition: all 500ms ease-in-out;

  &:hover {
    transform: translate3d(0, -4px, 0);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.4);
    background: #FFD93D;
    color: #2d3748;
  }

  @media (max-width: 800px) {
    width: 100%;
    padding: 18px 40px;
  }
`

const Section = styled.section`
  padding: 100px 0;

  @media (max-width: 800px) {
    padding: 60px 0;
  }
`

const OverviewSection = styled(Section)`
  background: linear-gradient(180deg, #f7fafc 0%, #edf2f7 100%);
`

const SectionTitle = styled.h2`
  font-size: 36px;
  font-weight: 700;
  line-height: 1.3;
  color: #1a202c;
  text-align: center;
  margin-bottom: 20px;

  @media (max-width: 800px) {
    font-size: 28px;
  }
`

const SectionSubtitle = styled.p`
  font-size: 18px;
  line-height: 1.8;
  color: #4a5568;
  margin-bottom: 30px;
  text-align: center;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
`

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 40px;
  margin-top: 60px;

  @media (max-width: 800px) {
    grid-template-columns: 1fr;
    gap: 30px;
  }
`

const FeatureCard = styled.div`
  background: white;
  padding: 40px;
  border-radius: 16px;
  box-shadow: 0px 4px 20px rgba(236, 72, 153, 0.1);
  transition: all 500ms ease-in-out;
  text-align: center;

  &:hover {
    transform: translate3d(0, -10px, 0);
    box-shadow: 0px 12px 40px rgba(236, 72, 153, 0.2);
  }
`

const FeatureIcon = styled.div`
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, #ec4899 0%, #8b5cf6 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 40px;
  margin: 0 auto 25px;
`

const FeatureTitle = styled.h3`
  font-size: 22px;
  font-weight: 700;
  color: #1a202c;
  margin-bottom: 15px;
`

const FeatureDescription = styled.p`
  font-size: 16px;
  line-height: 1.6;
  color: #4a5568;
`

const BenefitsSection = styled(Section)`
  background: linear-gradient(180deg, #ffffff 0%, #f7fafc 100%);
`

const BenefitsList = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 30px;
  margin-top: 60px;

  @media (max-width: 800px) {
    grid-template-columns: 1fr;
  }
`

const BenefitItem = styled.div`
  display: flex;
  gap: 20px;
  align-items: flex-start;
`

const BenefitIcon = styled.div`
  font-size: 32px;
  color: #ec4899;
  flex-shrink: 0;
`

const BenefitContent = styled.div``

const BenefitTitle = styled.h4`
  font-size: 20px;
  font-weight: 700;
  color: #1a202c;
  margin-bottom: 10px;
`

const BenefitText = styled.p`
  font-size: 16px;
  line-height: 1.6;
  color: #4a5568;
`

const HowItWorksSection = styled(Section)`
  background: linear-gradient(180deg, #edf2f7 0%, #e2e8f0 100%);
`

const StepsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 30px;
  margin-top: 60px;

  @media (max-width: 800px) {
    grid-template-columns: 1fr;
  }
`

const StepCard = styled.div`
  background: white;
  padding: 30px;
  border-radius: 12px;
  text-align: center;
  position: relative;
  box-shadow: 0px 4px 20px rgba(236, 72, 153, 0.1);
`

const StepNumber = styled.div`
  width: 50px;
  height: 50px;
  background: linear-gradient(135deg, #ec4899 0%, #8b5cf6 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  font-weight: 700;
  color: white;
  margin: 0 auto 20px;
`

const StepTitle = styled.h4`
  font-size: 18px;
  font-weight: 700;
  color: #1a202c;
  margin-bottom: 10px;
`

const StepDescription = styled.p`
  font-size: 15px;
  line-height: 1.6;
  color: #4a5568;
`

const CTASection = styled(Section)`
  background: linear-gradient(135deg, #ec4899 0%, #8b5cf6 100%);
  color: white;
  text-align: center;
`

const CTATitle = styled.h2`
  font-size: 36px;
  font-weight: 700;
  margin-bottom: 20px;
  color: white;

  @media (max-width: 800px) {
    font-size: 28px;
  }
`

const CTADescription = styled.p`
  font-size: 18px;
  margin-bottom: 40px;
  color: rgba(255, 255, 255, 0.95);
  max-width: 700px;
  margin-left: auto;
  margin-right: auto;
`

const CTAButtonWhite = styled(CTAButton)`
  background: white;
  color: #ec4899;

  &:hover {
    background: #FFD93D;
    color: #2d3748;
  }
`

const Azentris = () => {
  const navigate = useNavigate()

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  const features = [
    {
      icon: '⏱️',
      title: 'Timesheet Management',
      description: 'Capture and track employee timesheets effortlessly with automated time tracking, approval workflows, and real-time visibility into hours worked across all vendors and projects.'
    },
    {
      icon: '💳',
      title: 'Automated Billing & Invoicing',
      description: 'Generate accurate invoices automatically based on approved timesheets, rates, and contracts. Streamline billing processes and reduce manual errors with intelligent automation.'
    },
    {
      icon: '👥',
      title: 'Employee Onboarding & Data Management',
      description: 'Store comprehensive employee information including date of joining (DOJ), contact details, skills, certifications, and work history in a centralized, secure database.'
    },
    {
      icon: '🔗',
      title: 'Client & Project Mapping',
      description: 'Map employees to specific clients and projects with ease. Track resource allocation, project assignments, and maintain clear visibility of who is working on what and where.'
    },
    {
      icon: '📅',
      title: 'Contract Management',
      description: 'Monitor contract start dates, end dates, and terms for all vendor employees. Track contract status and manage renewals efficiently with automated tracking.'
    },
    {
      icon: '🔔',
      title: 'Contract Expiry Notifications',
      description: 'Receive automated alerts when employee contracts are approaching expiration. Stay ahead of contract renewals and avoid gaps in staffing with proactive notifications.'
    },
    {
      icon: '📊',
      title: 'Reporting & Analytics',
      description: 'Generate comprehensive reports on vendor performance, billing summaries, timesheet compliance, and resource utilization to make data-driven decisions.'
    },
    {
      icon: '🔐',
      title: 'Role-Based Access Control',
      description: 'Secure your vendor data with granular permission settings. Control who can view, edit, and approve timesheets, invoices, and employee information based on roles.'
    }
  ]

  const benefits = [
    {
      icon: '⚡',
      title: 'Eliminate Manual Processes',
      description: 'Automate timesheet approvals, invoice generation, and billing workflows to save hours of administrative work and reduce human error.'
    },
    {
      icon: '💰',
      title: 'Improve Cash Flow',
      description: 'Generate and send invoices faster with automated billing. Track payment status and reduce days sales outstanding (DSO) with streamlined processes.'
    },
    {
      icon: '📊',
      title: 'Complete Visibility',
      description: 'Gain real-time insights into vendor operations, resource utilization, billing status, and contract compliance across your entire organization.'
    },
    {
      icon: '🔔',
      title: 'Never Miss Contract Renewals',
      description: 'Stay ahead with proactive contract expiry notifications. Plan for extensions or replacements well in advance to maintain business continuity.'
    },
    {
      icon: '✅',
      title: 'Enhanced Compliance',
      description: 'Maintain audit trails, ensure timesheet accuracy, and track contract terms to meet regulatory requirements and internal compliance standards.'
    },
    {
      icon: '🚀',
      title: 'Scale Your Operations',
      description: 'Manage unlimited vendors, employees, clients, and projects without adding administrative overhead. Grow your business with confidence.'
    }
  ]

  const steps = [
    {
      number: '1',
      title: 'Onboard Employees',
      description: 'Add vendor employees to the system with complete details including DOJ, contact info, skills, and assign them to clients and projects.'
    },
    {
      number: '2',
      title: 'Track Timesheets',
      description: 'Employees submit timesheets, managers approve them, and the system automatically tracks hours worked across all projects and clients.'
    },
    {
      number: '3',
      title: 'Generate Invoices',
      description: 'Automatically create accurate invoices based on approved timesheets and contract rates. Send invoices to clients with one click.'
    },
    {
      number: '4',
      title: 'Manage Contracts',
      description: 'Monitor contract periods, receive expiry notifications, and decide on extensions or terminations to ensure seamless operations.'
    }
  ]

  return (
    <Container>
      <HeroSection>
        <MaxWidthContainer>
          <HeroContent>
            <ProductLogo>Azentris</ProductLogo>
            <HeroTagline>Complete Vendor Management System for Modern Businesses</HeroTagline>
            <HeroDescription>
              Azentris is a comprehensive vendor management platform that streamlines employee timesheet tracking, automates billing processes,
              generates invoices, and manages the complete employee lifecycle from onboarding to contract completion. Simplify vendor operations
              with intelligent automation and real-time notifications.
            </HeroDescription>
            <CTAButton onClick={() => navigate('/contact')}>
              Request a Demo
            </CTAButton>
          </HeroContent>
        </MaxWidthContainer>
      </HeroSection>

      <OverviewSection>
        <MaxWidthContainer>
          <SectionTitle>Streamline Your Vendor Operations End-to-End</SectionTitle>
          <SectionSubtitle>
            Azentris combines powerful automation with intuitive design to deliver a complete vendor management solution
            that handles everything from employee onboarding and timesheet tracking to billing, invoicing, and contract management.
            Transform how you manage vendors, employees, clients, and projects with one unified platform.
          </SectionSubtitle>

          <FeaturesGrid>
            {features.map((feature, index) => (
              <FeatureCard key={index}>
                <FeatureIcon>{feature.icon}</FeatureIcon>
                <FeatureTitle>{feature.title}</FeatureTitle>
                <FeatureDescription>{feature.description}</FeatureDescription>
              </FeatureCard>
            ))}
          </FeaturesGrid>
        </MaxWidthContainer>
      </OverviewSection>

      <BenefitsSection>
        <MaxWidthContainer>
          <SectionTitle>Why Businesses Choose Azentris</SectionTitle>
          <SectionSubtitle>
            Leading companies rely on Azentris to streamline vendor management operations, reduce administrative overhead,
            and gain complete visibility into their vendor workforce and billing processes.
          </SectionSubtitle>

          <BenefitsList>
            {benefits.map((benefit, index) => (
              <BenefitItem key={index}>
                <BenefitIcon>{benefit.icon}</BenefitIcon>
                <BenefitContent>
                  <BenefitTitle>{benefit.title}</BenefitTitle>
                  <BenefitText>{benefit.description}</BenefitText>
                </BenefitContent>
              </BenefitItem>
            ))}
          </BenefitsList>
        </MaxWidthContainer>
      </BenefitsSection>

      <HowItWorksSection>
        <MaxWidthContainer>
          <SectionTitle>How Azentris Works</SectionTitle>
          <SectionSubtitle>
            Getting started with Azentris is simple. Onboard your vendor employees, track timesheets, generate invoices,
            and manage contracts—all from one unified platform.
          </SectionSubtitle>

          <StepsGrid>
            {steps.map((step, index) => (
              <StepCard key={index}>
                <StepNumber>{step.number}</StepNumber>
                <StepTitle>{step.title}</StepTitle>
                <StepDescription>{step.description}</StepDescription>
              </StepCard>
            ))}
          </StepsGrid>
        </MaxWidthContainer>
      </HowItWorksSection>

      <CTASection>
        <MaxWidthContainer>
          <CTATitle>Transform Your Vendor Management Today</CTATitle>
          <CTADescription>
            Join leading companies that have streamlined their vendor operations with Azentris. Schedule a personalized demo
            and discover how Azentris can automate your timesheet tracking, billing, and contract management processes.
          </CTADescription>
          <CTAButtonWhite onClick={() => navigate('/contact')}>
            Request a Demo
          </CTAButtonWhite>
        </MaxWidthContainer>
      </CTASection>
    </Container>
  )
}

export default Azentris
